import type { ShellEventMap, Dispose } from '@opengov/psp-contract';

type Handler = (...args: unknown[]) => void;

/**
 * Typed event emitter for shell lifecycle events.
 * Each handler invocation is wrapped in try-catch so a failing
 * app handler cannot prevent other handlers from executing.
 */
export class ShellEventBus {
  private handlers = new Map<keyof ShellEventMap, Set<Handler>>();

  on<K extends keyof ShellEventMap>(event: K, handler: ShellEventMap[K]): Dispose {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    const handlerSet = this.handlers.get(event)!;
    handlerSet.add(handler as Handler);

    return () => {
      handlerSet.delete(handler as Handler);
    };
  }

  emit<K extends keyof ShellEventMap>(event: K, ...args: Parameters<ShellEventMap[K]>): void {
    const handlerSet = this.handlers.get(event);
    if (!handlerSet) return;

    for (const handler of handlerSet) {
      try {
        handler(...args);
      } catch (err) {
        console.error(`[ShellEventBus] Handler for "${String(event)}" threw:`, err);
      }
    }
  }
}
